import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { notificationsAPI } from '../api';
import { toast } from 'sonner';
import { 
  Bell, 
  MessageCircle, 
  Headphones,
  ArrowLeft, 
  Check,
  CheckCheck,
  Loader2,
  Inbox as InboxIcon
} from 'lucide-react';
import type { NotificationItem, NotificationListResponse } from '../types';

const InboxPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [markingRead, setMarkingRead] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadNotifications = useCallback(async (cursor?: string) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const response: NotificationListResponse = await notificationsAPI.getNotifications({
        limit: 20,
        cursor: cursor || undefined
      });
      
      if (cursor) {
        setNotifications(prev => [...prev, ...response.notifications]);
      } else {
        setNotifications(response.notifications);
      }
      
      setTotal(response.total);
      setHasMore(response.has_more);
      setNextCursor(response.next_cursor || null);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      await notificationsAPI.markRead({ mark_all: true });
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all read:', error);
      toast.error('Failed to mark notifications as read');
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark as read if not already
    if (!notification.is_read) {
      try {
        await notificationsAPI.markRead({ notification_ids: [notification.notification_id] });
        setNotifications(prev =>
          prev.map(n =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          )
        );
      } catch (error) {
        console.error('Error marking notification read:', error);
      }
    }
    
    // Navigate based on notification type
    if (notification.type === 'briefing' && notification.digest_id) {
      navigate(`/digests/${notification.digest_id}`);
    } else if (notification.thread_id) {
      navigate(`/community/thread/${notification.thread_id}`);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      <Card data-testid="inbox-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-gray-600" />
            <CardTitle className="text-xl">Inbox</CardTitle>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white border-0" data-testid="unread-badge">
                {unreadCount}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={markingRead}
              data-testid="mark-all-read-btn"
            >
              {markingRead ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Mark all read
                </>
              )}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-1">
          {notifications.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-inbox">
              <InboxIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No notifications yet</p>
              <p className="text-sm text-gray-400 mt-1">
                You'll be notified when someone replies to your comments
              </p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <button
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${
                    notification.is_read 
                      ? 'bg-white hover:bg-gray-50' 
                      : 'bg-blue-50 hover:bg-blue-100'
                  }`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      notification.is_read ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      {notification.type === 'briefing' ? (
                        <Headphones className={`h-4 w-4 ${notification.is_read ? 'text-gray-500' : 'text-blue-600'}`} />
                      ) : (
                        <MessageCircle className={`h-4 w-4 ${notification.is_read ? 'text-gray-500' : 'text-blue-600'}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${
                          notification.is_read ? 'text-gray-600' : 'text-gray-900 font-medium'
                        }`}>
                          {notification.summary_text}
                        </p>
                        {!notification.is_read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      {notification.thread_title && (
                        <p className="responsive-title text-xs text-gray-500 mt-0.5 truncate">
                          in "{notification.thread_title}"
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    {notification.is_read && (
                      <Check className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
              
              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => nextCursor && loadNotifications(nextCursor)}
                    disabled={loadingMore}
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        Showing {notifications.length} of {total} notifications
      </p>
    </div>
  );
};

export default InboxPage;
